<script setup lang="ts">
import type { VideoCardHeaderProps } from '.';

import {
	getFallbackBrokerImage,
	getFallbackCompanyImage
} from '@/utils/imageUrl';

import { BrokerAvatarInfo } from '~/components/BrokerAvatarInfo';
import { FavoriteButton } from '~/components/FavoriteButton';
import { GlassButton } from '~/components/GlassButton';

import IconMaterialSymbolsLightVolumeOff from '~icons/material-symbols-light/volume-off-outline';
import IconMaterialSymbolsLightVolumeUp from '~icons/material-symbols-light/volume-up-outline';

const props = defineProps<VideoCardHeaderProps>();

const { ORIGIN } = useRuntimeConfig().public;

const brokerSrc = computed(
	() => props.agentInfo.profilePictureUrl || getFallbackBrokerImage(ORIGIN)
);
const companySrc = computed(
	() => props.companyInfo?.logoUrl || getFallbackCompanyImage(ORIGIN)
);

const isFavorite = defineModel<boolean>('isFavorite');

defineEmits<{
	'toggle-mute': [];
	'toggle-favorite': [];
}>();
</script>

<template>
	<div
		:class="[
			'absolute inset-x-0 top-0 z-10 flex items-start gap-3 p-4 transition-opacity duration-500 ease-in-out',
			isVisible ? 'opacity-100' : 'pointer-events-none opacity-0'
		]"
	>
		<BrokerAvatarInfo
			v-if="agentInfo"
			class="flex-1 gap-7 overflow-hidden"
			avatar-shape="circle"
			avatar-class="size-8"
			sizes="linear"
			:broker-src="brokerSrc"
			:company-src="companySrc"
			:broker-name="agentInfo.name"
			:company-name="companyInfo?.name || ''"
			:info-classes="{
				wrapper: '[text-shadow:0_1px_3px_rgba(0,0,0,0.15)]',
				brokerName: 'text-sm font-medium text-white',
				companyName: 'text-xs text-white-dark-2'
			}"
		>
			<template #broker>{{ agentInfo.name }}</template>
			<template #company>{{ companyInfo?.name }}</template>
		</BrokerAvatarInfo>

		<div class="ml-auto flex shrink-0 flex-col gap-3">
			<ClientOnly>
				<template #fallback>
					<FavoriteButton
						class="size-10"
						:is-favorite="false"
						:property-id="propertyId"
						:commercial-id="commercialId"
					/>
				</template>
				<FavoriteButton
					v-if="isFavoriteValid"
					v-model:is-favorite="isFavorite"
					class="size-10"
					:property-id="propertyId"
					:commercial-id="commercialId"
					@toggle="$emit('toggle-favorite')"
				/>
			</ClientOnly>
			<GlassButton
				class="size-10"
				aria-label="Alternar som"
				@click.stop.prevent="$emit('toggle-mute')"
			>
				<IconMaterialSymbolsLightVolumeOff
					v-show="isMuted"
					class="size-5 text-white"
				/>
				<IconMaterialSymbolsLightVolumeUp
					v-show="!isMuted"
					class="size-5 text-white"
				/>
			</GlassButton>
		</div>
	</div>
</template>
